import { backdropAtom, selectedOrderListAtom, selectOrderModalAtom } from '@/commons/constants';
import { planOrderListAtom } from '@/interfaces/plan/orders';
import { Order } from '@/types/plan';
import { Button, Divider, Modal, Typography } from '@mui/material';
import { DataGrid, GridCloseIcon, GridColDef, GridRenderCellParams, useGridApiRef, gridPaginatedVisibleSortedGridRowIdsSelector } from '@mui/x-data-grid';
import ClearIcon from '@mui/icons-material/Clear';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as _ from 'lodash';
import { CustomTextField } from '../common/CustomMui';
import CustomDatePicker from '@/commons/CustomDatePicker';
import { Dayjs } from 'dayjs';

const ModalWrap = styled.div`
  position: absolute;
  top:50%;
  left:50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 90%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  .head{
    padding: 0.5rem;
    width: 100%;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background:rgba(82, 133, 164, 0.87);
    color: #ffffff;
  }
`;

export default function SelectOrderList() {
  const [selectOrderModal, setSelectOrderModal] = useAtom(selectOrderModalAtom);
  const [nowSelected, setNowSelected] = useState<any[]>([]);
  const [selectedOrderList, setSelectedOrderList] = useAtom(selectedOrderListAtom);
  const handleClose = () => {
    setSelectOrderModal(false);
  };

  const handleSave = () => {
    if (nowSelected && nowSelected.length > 0) {
      setSelectedOrderList([...selectedOrderList, ...nowSelected]);
      setNowSelected([]);
    }
    handleClose();
  };

  return (
    <Modal open={selectOrderModal} onClose={handleClose}>
      <ModalWrap>
        <div className="head">
          <Typography variant="h5">주문 목록</Typography>
          <GridCloseIcon onClick={handleClose} />
        </div>
        <Divider />
        <MainContent setNowSelected={setNowSelected} handleSave={handleSave} />
      </ModalWrap>
    </Modal>
  );
}

const MainContentWrap = styled.div`
  width: 100%;
  height: 100%;
  flex-grow:1;
  .body{
    height: calc(100% - 9rem);
    display: flex;
    justify-content: center;
    > div {
      width: 100%;
      padding: 0rem 2rem;
      height: 100%;
    }
`;

const FilterArea = styled.div`
  width: 100%;
  display:flex;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 1rem 2rem;
  > .filterColumn {
   display:flex;
    justify-content: flex-start;
    gap: 0.5rem;
  }
`;

const MainContent = ({
  setNowSelected,
  handleSave,
}: { setNowSelected: React.Dispatch<React.SetStateAction<Order[]>>; handleSave: any }) => {
  const { data: orderList, isLoading } = useAtomValue(planOrderListAtom);
  const selectedOrderList = useAtomValue(selectedOrderListAtom);
  const setBackdrop = useSetAtom(backdropAtom);

  const [contents, setContents] = useState<any[]>();

  const [filteredContents, setFilteredContents] = useState<any[]>();

  const [filterOrderDate, setFilterOrderDate] = useState<String>();
  const [filterScanCode, setFilterScanCode] = useState<String>();
  const [filterName, setFilterName] = useState<String>();
  const [filterAddress, setFilterAddress] = useState<String>();
  const [filterFileName, setFilterFileName] = useState<String>();

  const column: GridColDef[] = [
    { field: 'scan_code', headerName: '주문번호', width: 120, headerAlign: 'center' },
    { field: 'order_date', headerName: '주문일', width: 110, headerAlign: 'center', align: 'center' },
    { field: 'name', headerName: '배송지', width: 250, headerAlign: 'center' },
    { field: 'address', headerName: '주소', flex: 1, headerAlign: 'center' },
    {
      field: 'order_count',
      headerName: '제품종류',
      width: 110,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (value?: number) => {
        if (value == null) {
          return '';
        }
        return value + '종류';
      },
    },
    {
      field: 'volume',
      headerName: '체적(L)',
      width: 80,
      headerAlign: 'center',
      align: 'center',
      renderCell: (param?: GridRenderCellParams<any, number>) => {
        return (
          <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {param.value == null ? <ClearIcon htmlColor="#d77" /> : param.value}
          </div>
        );
      },
    },
    { field: 'file_name', headerName: '업로드파일명', width: 160, headerAlign: 'center' },
    { field: 'create_dt', headerName: '등록일', width: 160, headerAlign: 'center' },
  ];

  useEffect(() => {
    if (isLoading) {
      setBackdrop(true);
    } else {
      setBackdrop(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (orderList && orderList.length > 0 && selectedOrderList) {
      const temp = _.sortBy(orderList, 'order_sheet_id')
        .reverse()
        .map((item: Order, index: number) => {
          return {
            ...item,
            id: item.order_sheet_id,
          };
        });
      const orgContent = _.differenceBy(temp, selectedOrderList, 'id');
      setContents(orgContent);
      setFilteredContents(orgContent);
    }
  }, [orderList, selectedOrderList]);

  useEffect(() => {
    if (contents && contents.length > 0) {
      let target = contents;
      if (filterOrderDate && filterOrderDate !== null) {
        target = _.filter(target, ['order_date', filterOrderDate]);
      }
      if (filterScanCode && filterScanCode !== null) {
        target = _.filter(target, function (o) {
          return o.scan_code.indexOf(filterScanCode) > -1;
        });
      }
      if (filterName && filterName !== null) {
        target = _.filter(target, function (o) {
          return o.name.indexOf(filterName) > -1;
        });
      }
      if (filterAddress && filterAddress !== null) {
        target = _.filter(target, function (o) {
          return o.address.indexOf(filterAddress) > -1;
        });
      }
      if (filterFileName && filterFileName !== null) {
        target = _.filter(target, function (o) {
          return o.file_name.indexOf(filterFileName) > -1;
        });
      }
      setFilteredContents(target);
    }
  }, [filterOrderDate, filterScanCode, filterName, filterAddress, filterFileName]);

  const apiRef = useGridApiRef();

  const handleSelectionModelChange = (val: any) => {
    const temp = val.map((item: number) => {
      return { id: item };
    });
    const target = _.intersectionBy(contents, temp, 'id');
    setNowSelected(target);
  };

  const handleSelectPageClick = () => {
    const pageIds = gridPaginatedVisibleSortedGridRowIdsSelector(apiRef);
    const current = new Set(apiRef.current.getSelectedRows().keys());
    const allSelected = pageIds.every((id) => current.has(id));
    if (allSelected) {
      pageIds.forEach((id) => current.delete(id));
    } else {
      pageIds.forEach((id) => current.add(id));
    }
    apiRef.current.setRowSelectionModel([...current]);
  };

  const handleSelectAllClick = () => {
    const allIds = (filteredContents ?? []).map((row) => row.id);
    const current = apiRef.current.getSelectedRows();
    if (current.size === allIds.length) {
      apiRef.current.setRowSelectionModel([]);
    } else {
      apiRef.current.setRowSelectionModel(allIds);
    }
  };

  const handleDatePickerChange = (p1: Dayjs) => {
    setFilterOrderDate(p1 ? p1.format('YYYY-MM-DD') : null);
  };

  return (
    <MainContentWrap>
      <FilterArea>
        <div className="filterColumn">
          <CustomDatePicker label="주문일" size="small" onChange={(value: Dayjs) => handleDatePickerChange(value)} />
          <CustomTextField
            size="small"
            label="주문번호"
            variant="outlined"
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => setFilterScanCode(e.target.value)}
          />
          <CustomTextField
            size="small"
            label="배송지"
            variant="outlined"
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => setFilterName(e.target.value)}
          />
          <CustomTextField
            size="small"
            label="주소"
            variant="outlined"
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => setFilterAddress(e.target.value)}
          />
          <CustomTextField
            size="small"
            label="업로드 파일명"
            variant="outlined"
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => setFilterFileName(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button variant="outlined" size="small" onClick={handleSelectPageClick}>
            현재 페이지 선택
          </Button>
          <Button variant="outlined" size="small" onClick={handleSelectAllClick}>
            전체 선택
          </Button>
          <Button variant="contained" onClick={handleSave}>
            선택 불러오기
          </Button>
        </div>
      </FilterArea>
      <div className="body">
        <div>
          <DataGrid
            apiRef={apiRef}
            checkboxSelection
            keepNonExistentRowsSelected
            disableColumnResize
            disableRowSelectionOnClick
            columns={column}
            rows={filteredContents && filteredContents.length > 0 ? filteredContents : []}
            onRowSelectionModelChange={(val) => handleSelectionModelChange(val)}
            sx={{ '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': { display: 'none' } }}
          />
        </div>
      </div>
    </MainContentWrap>
  );
};
